import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CleaningAreaMapOverlayProps } from '../../../../../../../types/CleaningAreaMapOverlay.types';
import { CleaningAreaMapFeatures } from './CleaningAreaMapFeatures';
import {
  CleaningAreaLabel,
  CleaningAreaLabelBadge,
  CleaningAreaLabelText,
  CleaningAreaMapOverlayContainer,
  CleaningAreaMapOverlayFrame,
  CleaningAreaMapOverlayImage,
  CleaningAreaOutlinePolygon,
  CleaningAreaOutlineSvg,
} from './CleaningAreaMapOverlay.styles';

export const CleaningAreaMapOverlay = ({
  mapImageSrc,
  mapName,
  mapWidthPx,
  mapHeightPx,
  areas,
  features,
  selectedAreaIds,
  onToggleArea,
  disabled,
  onRenderedHeightChange,
  onImageError,
  hideStartMarkerRobot,
  areaStyle = 'default',
}: CleaningAreaMapOverlayProps): JSX.Element => {
  const { t } = useTranslation();

  // Frame needs the image's aspect ratio, or the percentage-based outlines/labels misalign.
  const aspectRatio = mapWidthPx && mapHeightPx ? mapWidthPx / mapHeightPx : undefined;

  // Reports the map's rendered height so the area list beside it can match it.
  const frameRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !onRenderedHeightChange) {
      return undefined;
    }

    let lastHeight = -1;
    const report = (): void => {
      const height = Math.round(frame.getBoundingClientRect().height);
      if (height !== lastHeight) {
        lastHeight = height;
        onRenderedHeightChange(height);
      }
    };

    report();
    const observer = new ResizeObserver(report);
    observer.observe(frame);
    return (): void => observer.disconnect();
  }, [onRenderedHeightChange]);

  return (
    <CleaningAreaMapOverlayContainer>
      <CleaningAreaMapOverlayFrame ref={frameRef} $aspectRatio={aspectRatio}>
        <CleaningAreaMapOverlayImage src={mapImageSrc} alt={mapName} draggable={false} onError={onImageError} />

        <CleaningAreaMapFeatures features={features} hideStartMarkerRobot={hideStartMarkerRobot} />

        <CleaningAreaOutlineSvg viewBox="0 0 100 100" preserveAspectRatio="none">
          {areas.map(area => {
            const isSelected = selectedAreaIds.includes(area.id);

            return (
              <CleaningAreaOutlinePolygon
                key={area.id}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-label={t('createSchedule.selectArea', { areaName: area.name })}
                aria-pressed={isSelected}
                points={area.polygon.map(point => `${point.x},${point.y}`).join(' ')}
                $isSelected={isSelected}
                $areaStyle={areaStyle}
                $disabled={disabled}
                onClick={disabled ? undefined : (): void => onToggleArea(area.id)}
                onKeyDown={
                  disabled
                    ? undefined
                    : (event): void => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onToggleArea(area.id);
                        }
                      }
                }
              />
            );
          })}
        </CleaningAreaOutlineSvg>

        {areas.map(area => {
          const position = selectedAreaIds.indexOf(area.id);
          const isSelected = position !== -1;

          return (
            <CleaningAreaLabel key={area.id} style={{ left: `${area.position.x}%`, top: `${area.position.y}%` }}>
              {isSelected && <CleaningAreaLabelBadge count={position + 1} />}
              <CleaningAreaLabelText $isSelected={isSelected} $areaStyle={areaStyle}>
                {area.name}
              </CleaningAreaLabelText>
            </CleaningAreaLabel>
          );
        })}
      </CleaningAreaMapOverlayFrame>
    </CleaningAreaMapOverlayContainer>
  );
};
